import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { approveNews, rejectNews } from "@/lib/news.functions";
import { translateNewsItem } from "@/lib/translate.functions";
import { sendNewsToTelegram } from "@/lib/telegram.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, X, Languages, Send, Pencil, Save } from "lucide-react";

export const Route = createFileRoute("/_authenticated/approval")({ component: Page });

function Page() {
  const qc = useQueryClient();
  const ap = useServerFn(approveNews); const rj = useServerFn(rejectNews);
  const tr = useServerFn(translateNewsItem); const sd = useServerFn(sendNewsToTelegram);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const { data } = useQuery({
    queryKey: ["pending_news"],
    queryFn: async () => (await supabase.from("news_items").select("*, asset_categories(name_th, icon), translated_news(*)").eq("approval_status", "pending").order("created_at", { ascending: false }).limit(50)).data ?? [],
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["pending_news"] });
  const startEdit = (tnId: string, msg: string) => setEditing((p) => ({ ...p, [tnId]: msg }));
  const cancelEdit = (tnId: string) => setEditing((p) => { const n = { ...p }; delete n[tnId]; return n; });
  const saveMsg = async (tnId: string) => {
    setSaving((p) => ({ ...p, [tnId]: true }));
    const { error } = await supabase.from("translated_news").update({ telegram_message: editing[tnId] }).eq("id", tnId);
    setSaving((p) => ({ ...p, [tnId]: false }));
    if (error) return toast.error(error.message);
    toast.success("บันทึกข้อความแล้ว");
    cancelEdit(tnId);
    refresh();
  };
  const doApprove = async (id: string) => { try { await ap({ data: { id } }); toast.success("อนุมัติแล้ว"); refresh(); } catch (e: any) { toast.error(e.message); } };
  const doReject = async (id: string) => { const r = prompt("เหตุผล (ไม่บังคับ)") ?? undefined; try { await rj({ data: { id, reason: r } }); toast.success("ปฏิเสธแล้ว"); refresh(); } catch (e: any) { toast.error(e.message); } };
  const doTr = async (id: string) => { try { toast.loading("แปล...", { id: "x" }); await tr({ data: { newsItemId: id } }); toast.success("แปลแล้ว", { id: "x" }); refresh(); } catch (e: any) { toast.error(e.message, { id: "x" }); } };
  const doSend = async (id: string) => { try { toast.loading("ส่ง...", { id: "y" }); const r: any = await sd({ data: { newsItemId: id } }); toast.success(`ส่ง ${r.success}/${r.total}`, { id: "y" }); refresh(); } catch (e: any) { toast.error(e.message, { id: "y" }); } };

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-display font-semibold">Approval Queue</h1><p className="text-sm text-muted-foreground">ข่าวที่รออนุมัติก่อนส่ง Telegram</p></div>
      <div className="grid gap-4">
        {(data ?? []).map((n: any) => {
          const tn = n.translated_news?.[0];
          return (
            <Card key={n.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base">{n.asset_categories?.icon} {n.original_title}</CardTitle>
                    <CardDescription className="mt-1">{n.asset_categories?.name_th} · {new Date(n.created_at).toLocaleString("th-TH")}</CardDescription>
                  </div>
                  <Badge variant="outline">pending</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-muted-foreground line-clamp-3">{n.original_summary}</div>
                {tn && (
                  <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm space-y-2">
                    <div className="font-medium">🇹🇭 {tn.title_th}</div>
                    {tn.summary_th && <div className="text-muted-foreground">{tn.summary_th}</div>}
                    <div className="border-t border-primary/10 pt-2">
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs text-muted-foreground">ข้อความ Telegram (พร้อมส่ง)</Label>
                        {editing[tn.id] === undefined ? (
                          <Button size="sm" variant="ghost" onClick={() => startEdit(tn.id, tn.telegram_message ?? "")}>
                            <Pencil className="h-3 w-3 mr-1"/>แก้ไข
                          </Button>
                        ) : (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => cancelEdit(tn.id)}>ยกเลิก</Button>
                            <Button size="sm" onClick={() => saveMsg(tn.id)} disabled={saving[tn.id]}>
                              <Save className="h-3 w-3 mr-1"/>บันทึก
                            </Button>
                          </div>
                        )}
                      </div>
                      {editing[tn.id] !== undefined ? (
                        <Textarea
                          rows={8}
                          className="font-mono text-xs"
                          value={editing[tn.id]}
                          onChange={(e) => setEditing((p) => ({ ...p, [tn.id]: e.target.value }))}
                        />
                      ) : (
                        <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground bg-background/50 p-2 rounded border border-border/50">{tn.telegram_message}</pre>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {!tn && <Button size="sm" variant="outline" onClick={() => doTr(n.id)}><Languages className="h-3 w-3 mr-1"/>แปลก่อน</Button>}
                  <Button size="sm" onClick={() => doApprove(n.id)}><Check className="h-3 w-3 mr-1"/>อนุมัติ</Button>
                  <Button size="sm" variant="destructive" onClick={() => doReject(n.id)}><X className="h-3 w-3 mr-1"/>ปฏิเสธ</Button>
                  {tn && <Button size="sm" variant="outline" onClick={() => doSend(n.id)}><Send className="h-3 w-3 mr-1"/>อนุมัติ + ส่งเลย</Button>}
                  <a className="ml-auto text-xs text-primary underline self-center" href={n.original_url} target="_blank" rel="noreferrer">อ่านต้นฉบับ</a>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {(data ?? []).length === 0 && <Card><CardContent className="py-12 text-center text-muted-foreground">ไม่มีข่าวรออนุมัติ</CardContent></Card>}
      </div>
    </div>
  );
}